import {
  Button,
  Callout,
  FormGroup,
  HTMLTable,
  InputGroup,
  Intent,
  Tooltip,
} from "@blueprintjs/core";
import { useState } from "react";
import { ApiError } from "../../../api/ApiClient";
import { useAdminFetch, useAdminQuery } from "../../api/AdminClient";
import { useMutation } from "../../api/useMutation";
import { AdminErrorMessage } from "../../shared/AdminErrorMessage";
import type { GeneratorFixtureVehicle } from "./fixtureModels";

interface VehicleLookupResult {
  licensePlateNumber: string;
  manufactureName: string | null;
  modelName: string | null;
  vehicleType: string | null;
}

interface PendingVehicle {
  licensePlateNumber: string;
  manufactureName: string;
  modelName: string;
  vehicleType: string;
  comment: string;
  isManualEntry: boolean;
}

interface SaveGeneratorFixtureVehicleRequest {
  licensePlateNumber: string;
  manufactureName: string | null;
  modelName: string | null;
  vehicleType: string | null;
  comment: string | null;
}

const VEHICLES_PATH = "admin/reportGenerator/fixtures/vehicles";

function findExistingVehicle(
  vehicles: GeneratorFixtureVehicle[] | null,
  licensePlateNumber: string,
): GeneratorFixtureVehicle | undefined {
  return vehicles?.find(
    (v) => v.licensePlateNumber.toLowerCase() === licensePlateNumber.toLowerCase(),
  );
}

// Builds the form state for a plate that was just looked up. manufactureName, modelName and
// vehicleType are all sourced from AutoTech the same way, so all three prefer the fresh lookup
// `result` and fall back to whatever was already saved for that plate (`existing`) - which
// matters when `result` is missing (404) or AutoTech didn't return a value for that field.
// `comment` has no AutoTech source at all, so it only ever comes from `existing`. Falling back
// to `existing` in every case (rather than blanking the form) is what stops re-looking-up an
// already-saved plate from silently wiping its previously saved fields.
function buildPendingVehicle(
  licensePlateNumber: string,
  existing: GeneratorFixtureVehicle | undefined,
  result: VehicleLookupResult | undefined,
  isManualEntry: boolean,
): PendingVehicle {
  return {
    licensePlateNumber,
    manufactureName: result?.manufactureName ?? existing?.manufactureName ?? "",
    modelName: result?.modelName ?? existing?.modelName ?? "",
    vehicleType: result?.vehicleType ?? existing?.vehicleType ?? "",
    comment: existing?.comment ?? "",
    isManualEntry,
  };
}

function DeleteVehicleButton({
  id,
  onDeleted,
}: {
  id: string;
  onDeleted: () => void;
}) {
  const remove = useMutation<undefined, void>({
    path: `${VEHICLES_PATH}/${id}`,
    method: "DELETE",
  });

  return (
    <Tooltip content="Remove this saved vehicle from the pool">
      <Button
        icon="trash"
        type="button"
        minimal
        loading={remove.result.loading}
        onClick={async () => {
          await remove.fetch();
          onDeleted();
        }}
      />
    </Tooltip>
  );
}

export function GeneratorVehiclesPanel() {
  const [licensePlateNumber, setLicensePlateNumber] = useState("");
  const [pending, setPending] = useState<PendingVehicle | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<Error | null>(null);

  const list = useAdminQuery<GeneratorFixtureVehicle[]>(VEHICLES_PATH);
  const { fetch: adminFetch } = useAdminFetch();

  const save = useMutation<SaveGeneratorFixtureVehicleRequest, GeneratorFixtureVehicle>({
    path: VEHICLES_PATH,
    method: "POST",
    body: pending
      ? {
          licensePlateNumber: pending.licensePlateNumber.trim(),
          manufactureName: pending.manufactureName || null,
          modelName: pending.modelName || null,
          vehicleType: pending.vehicleType || null,
          comment: pending.comment || null,
        }
      : undefined,
  });

  // useMutation swallows fetch failures into its own state instead of rejecting the returned
  // promise, so it can't be used here - we need to inspect the failure synchronously, within
  // this same call, to tell "no AutoTech match" (404) apart from a real failure. useAdminFetch
  // is the lower-level hook that actually throws, so we call it directly and manage loading/error
  // state ourselves.
  async function onLookup() {
    if (!licensePlateNumber) return;
    setLookupLoading(true);
    setLookupError(null);
    try {
      const result = (await adminFetch({
        path: `admin/reportGenerator/vehicleLookup?licensePlateNumber=${encodeURIComponent(licensePlateNumber)}`,
      })) as VehicleLookupResult;
      const existing = findExistingVehicle(list.data, result.licensePlateNumber);
      setPending(buildPendingVehicle(result.licensePlateNumber, existing, result, false));
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        const existing = findExistingVehicle(list.data, licensePlateNumber);
        setPending(buildPendingVehicle(licensePlateNumber, existing, undefined, true));
      } else {
        setPending(null);
        setLookupError(error as Error);
      }
    } finally {
      setLookupLoading(false);
    }
  }

  function updatePending(
    field: "licensePlateNumber" | "manufactureName" | "modelName" | "vehicleType" | "comment",
    value: string,
  ) {
    if (!pending) return;
    setPending({ ...pending, [field]: value });
  }

  async function onSave() {
    if (!pending) return;
    const saved = await save.fetch();
    if (saved) {
      setPending(null);
      setLicensePlateNumber("");
      list.fetch({ path: VEHICLES_PATH });
    }
  }

  return (
    <div>
      <h3>Look up and save a vehicle</h3>
      <FormGroup label="License plate" labelFor="vehicleLicensePlateNumber" inline>
        <InputGroup
          id="vehicleLicensePlateNumber"
          disabled={lookupLoading}
          value={licensePlateNumber}
          onChange={(e) => setLicensePlateNumber(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onLookup();
          }}
          rightElement={
            <Button
              type="button"
              disabled={!licensePlateNumber}
              loading={lookupLoading}
              onClick={onLookup}
            >
              Hent opplysninger
            </Button>
          }
        />
      </FormGroup>
      {lookupError ? <AdminErrorMessage>{lookupError.message}</AdminErrorMessage> : null}
      {pending?.isManualEntry ? (
        <Callout intent={Intent.WARNING}>
          No match found in AutoTech — enter the vehicle's details manually below.
        </Callout>
      ) : null}
      {pending ? (
        <>
          {pending.isManualEntry ? (
            <FormGroup label="License plate" labelFor="vehiclePendingLicensePlateNumber" inline>
              <InputGroup
                id="vehiclePendingLicensePlateNumber"
                value={pending.licensePlateNumber}
                onChange={(e) => updatePending("licensePlateNumber", e.target.value)}
              />
            </FormGroup>
          ) : null}
          <FormGroup label="Vehicle type" labelFor="vehicleType" inline>
            <InputGroup
              id="vehicleType"
              value={pending.vehicleType}
              onChange={(e) => updatePending("vehicleType", e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Manufacturer" labelFor="vehicleManufactureName" inline>
            <InputGroup
              id="vehicleManufactureName"
              value={pending.manufactureName}
              onChange={(e) => updatePending("manufactureName", e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Model" labelFor="vehicleModelName" inline>
            <InputGroup
              id="vehicleModelName"
              value={pending.modelName}
              onChange={(e) => updatePending("modelName", e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Comment" labelFor="vehicleComment" inline>
            <InputGroup
              id="vehicleComment"
              value={pending.comment}
              onChange={(e) => updatePending("comment", e.target.value)}
            />
          </FormGroup>
          <Button
            type="button"
            disabled={!pending.licensePlateNumber.trim()}
            loading={save.result.loading}
            onClick={onSave}
          >
            Save to pool
          </Button>
        </>
      ) : null}
      {save.result.error ? (
        <AdminErrorMessage>{save.result.error.message}</AdminErrorMessage>
      ) : null}

      <h3>Saved vehicles</h3>
      {list.error ? <AdminErrorMessage>{list.error.message}</AdminErrorMessage> : null}
      {list.data && list.data.length === 0 ? <Callout>No saved vehicles yet.</Callout> : null}
      {list.data && list.data.length > 0 ? (
        <HTMLTable striped>
          <thead>
            <tr>
              <th>License plate</th>
              <th>Vehicle type</th>
              <th>Manufacturer</th>
              <th>Model</th>
              <th>Comment</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data.map((vehicle) => (
              <tr key={vehicle.id}>
                <td>{vehicle.licensePlateNumber}</td>
                <td>{vehicle.vehicleType}</td>
                <td>{vehicle.manufactureName}</td>
                <td>{vehicle.modelName}</td>
                <td>{vehicle.comment}</td>
                <td>
                  <DeleteVehicleButton
                    id={vehicle.id}
                    onDeleted={() => list.fetch({ path: VEHICLES_PATH })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </HTMLTable>
      ) : null}
    </div>
  );
}
